import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription, firstValueFrom, filter } from 'rxjs';
import { settledCache } from '../../shared/utils/cache-ready';

import { Coi } from '../../objects/coi';
import { CreateEntity } from '../create-entity';
import { EntityType, DatabaseAction } from '../../objects/interface-library';

// Material imports
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption } from '@angular/material/core';

// Services
import { CoiService } from '../../service/entity-services/coi.service';
import { CommonSheetsService } from '../../service/google-sheets/common-sheets.service';
import { CoiSheetsService } from '../../service/google-sheets/coi-sheets.service';
import { FileUploadService } from '../../service/files-and-attachments/file-upload.service';
import { RelationshipService } from '../../service/entity-data-services/relationship.service';
import { IntakeAnalysisResponse } from '../../service/ai/document-analysis.service';

// Components
import { DocumentViewerSidenavComponent } from '../../entity-form-components/document-viewer-sidenav/document-viewer-sidenav.component';

// Utilities
import { generateCoiId } from '../../utils/id-generator';

import { PartyLabelPipe } from '../../shared/pipes/party-label.pipe';
@Component({
  selector: 'app-new-coi-full-page',
  templateUrl: './new-coi-full-page.component.html',
  styleUrls: ['./new-coi-full-page.component.css'],
  standalone: true,
  imports: [
    PartyLabelPipe,
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatCardModule,
    MatCheckboxModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatToolbarModule,
    MatChipsModule,
    MatDividerModule,
    MatProgressSpinner,
    MatOption,
    DocumentViewerSidenavComponent
  ]
})
export class NewCoiFullPageComponent extends CreateEntity implements OnInit, OnDestroy {
  private commonSheetsService = inject(CommonSheetsService);
  private coiSheetsService = inject(CoiSheetsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private fileUploadService = inject(FileUploadService);
  private relationshipService = inject(RelationshipService);
  private cdr = inject(ChangeDetectorRef);

  // Form management
  form: FormGroup;
  private formSubscription!: Subscription;
  
  currentCoi!: Coi;
  originalCoi: any = {};
  
  // UI State
  isEditMode = false;

  // File handling properties moved to CreateEntity base class

  // Static field data
  statusTypes: any[] = [];
  allCoverageTypes: any[] = [];
  allDepartments: any[] = [];
  sites: any[] = [];
  
  saving = false;
  loadingCoiData = false;

  // Workflow Request front-door: AI analysis state
  fromWorkflowRequest = false;
  intakeAnalysisResults: IntakeAnalysisResponse | null = null;
  showAISummary = false;
  aiSummary = '';
  autoPopulatedFieldsDisplay: { label: string; value: string; confidence: number }[] = [];

  constructor() {
    super();
    
    // Initialize the form with validation
    this.form = this.fb.group({
      // IDs
      coiId: ['', Validators.required],
      coiNum: [''],
      mainDocumentName: [''],
      
      // Parties
      partyProvidingCoi: ['', Validators.required],
      insuredParty: [''],
      insuranceProvider: [''],
      
      // Legal entities and users
      legalEntities: [[]],
      internalCoordinator: [''],
      primaryOwner: [''],
      secondaryOwner: [''],
      tertiaryOwner: [''],
      internalSignatory: [''],
      coiCoordinator: [''],
      externalCoordinator: [''],
      carrierSignatory: [''],
      
      // Coverage and status
      coverageTypes: [[]],
      status: [''],
      
      // Dates
      effectiveDate: ['', Validators.required],
      expirationDate: [''],
      nextReviewDate: [''],
      lastReviewDate: [''],
      
      // Additional fields
      departments: [[]],
      sites: [[]],
      description: ['']
    });
  }

  async ngOnInit() {
    // Set loading state immediately when component initializes
    this.loadingCoiData = true;

    try {
      const coiNum = this.route.snapshot.paramMap.get('id');
      const navigation = this.router.getCurrentNavigation();
      const state = navigation?.extras?.state || history.state;

      // Load static field values
      await this.getStaticFieldValues();

      // Load autocomplete data for dropdown filtering.
      //
      // settledCache() waits for each paginated cache to FINISH streaming. The old
      // `filter(arr => arr.length > 0)` took the first non-empty emission, which is
      // page 1 — around 100 of cbhi's ~4,500 vendors — so most parties were simply
      // absent from these dropdowns and never appeared. It also can't deadlock on a
      // genuinely empty service the way a bare filter() does.
      [
        this.filteredUsers,
        this.filteredPeople,
        this.filteredSites,
        this.filteredLegalEntities,
        this.filteredParties,
      ] = await Promise.all([
        settledCache(this.allUsers$),
        settledCache(this.allPeople$),
        settledCache(this.allSites$),
        settledCache(this.allLegalEntities$),
        settledCache(this.allParties$),
      ]);

      if (coiNum) {
        // Load COI from service using ID
        this.isEditMode = true;
        await this.loadCoiByNum(coiNum);
      } else if (state?.coi) {
        // COI passed via router state
        this.isEditMode = true;
        this.currentCoi = state.coi;
        this.originalCoi = { ...state.coi };
        this.populateFormWithCoi(this.currentCoi);
      } else {
        this.isEditMode = false;
        // Brand new COI gets new coiId and coiNum
        this.form.patchValue({
          coiId: this.generateCoiId(),
          coiNum: Date.now().toString()
        });
      }

      // Workflow Request front-door: accept a file pre-selected by the intake dialog.
      const prefilledFile = state?.prefilledFile as File | undefined;
      if (prefilledFile instanceof File) {
        this.handleFileUpload(prefilledFile);
        this.fromWorkflowRequest = true;

        const existingAnalysis = state?.intakeAnalysisResult;
        if (existingAnalysis?.status === 'complete' && existingAnalysis?.extraction) {
          console.log('%c[NewCoi] Reusing AI analysis from workflow-request dialog',
            'color:#1976d2;font-weight:bold;', existingAnalysis);
          setTimeout(() => this.applyExistingAnalysis(existingAnalysis), 0);
        }
      }

      // Subscribe to form changes for tracking edits
      this.formSubscription = this.form.valueChanges.subscribe(changes => {
        this.currentCoi = { ...this.currentCoi, ...changes };
      });

    } finally {
      // Always turn off loading state when done (success or error)
      this.loadingCoiData = false;

      // Force change detection to update the UI
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  // ----- AI Analysis auto-fill (from workflow-request dialog) -----

  private applyExistingAnalysis(response: IntakeAnalysisResponse): void {
    this.intakeAnalysisResults = response;
    this.aiSummary = response.extraction?.summary || 'COI analyzed successfully.';
    this.showAISummary = true;
    this.autoFillCoiFromExtraction(response);
    this.cdr.detectChanges();
  }

  private autoFillCoiFromExtraction(response: IntakeAnalysisResponse): void {
    const ext = response.extraction;
    if (!ext) return;

    const coiFields = ext.coiFields;
    const updates: any = {};
    this.autoPopulatedFieldsDisplay = [];

    // Common fields
    if (ext.effectiveDate?.normalized) {
      updates.effectiveDate = new Date(ext.effectiveDate.normalized + 'T00:00:00');
      this.autoPopulatedFieldsDisplay.push({
        label: 'Effective Date',
        value: ext.effectiveDate.extracted || ext.effectiveDate.normalized,
        confidence: ext.effectiveDate.confidence || 0
      });
    }
    if (ext.expirationDate?.normalized) {
      updates.expirationDate = new Date(ext.expirationDate.normalized + 'T00:00:00');
      this.autoPopulatedFieldsDisplay.push({
        label: 'Expiration Date',
        value: ext.expirationDate.extracted || ext.expirationDate.normalized,
        confidence: ext.expirationDate.confidence || 0
      });
    }

    // COI-specific fields
    if (coiFields) {
      if (coiFields.insuredParty) {
        updates.insuredParty = coiFields.insuredParty;
        this.autoPopulatedFieldsDisplay.push({ label: 'Insured Party', value: coiFields.insuredParty, confidence: 0.9 });
      }
      if (coiFields.insuranceProvider) {
        updates.insuranceProvider = coiFields.insuranceProvider;
        this.autoPopulatedFieldsDisplay.push({ label: 'Insurance Provider', value: coiFields.insuranceProvider, confidence: 0.9 });
      }
      if (coiFields.partyProvidingCoi) {
        updates.partyProvidingCoi = coiFields.partyProvidingCoi;
        this.autoPopulatedFieldsDisplay.push({ label: 'Party Providing COI', value: coiFields.partyProvidingCoi, confidence: 0.85 });
      }
      if (coiFields.coverageTypes?.length) {
        updates.coverageTypes = coiFields.coverageTypes;
        this.autoPopulatedFieldsDisplay.push({ label: 'Coverage Types', value: coiFields.coverageTypes.join(', '), confidence: 0.85 });
      }
      if (coiFields.policyNumber) {
        // No form field for policyNumber — display only in the summary
        this.autoPopulatedFieldsDisplay.push({ label: 'Policy Number', value: coiFields.policyNumber, confidence: 0.9 });
      }
      if (coiFields.description) {
        updates.description = coiFields.description;
        this.autoPopulatedFieldsDisplay.push({ label: 'Description', value: coiFields.description, confidence: 0.8 });
      }
    }

    if (Object.keys(updates).length > 0) {
      this.form.patchValue(updates);
      console.log('%c[NewCoi] Auto-filled from AI extraction:', 'color:#4caf50;font-weight:bold;', updates);
    }
  }

  async loadCoiByNum(coiNum: string) {
    this.loadingCoiData = true;

    try {
      // settledCache, not firstValueFrom — the cache is filled page by page, so a
      // COI outside the first page fell through to the "COI not found" branch and
      // the edit bounced back to the list.
      const cois = await settledCache(this.coiService.visibleCois$);
      const coi = cois.find(c => c.coiNum === coiNum);

      if (coi) {
        //console.log('COI found:', coi);
        this.currentCoi = coi;
        this.originalCoi = { ...coi };
        this.populateFormWithCoi(coi);
      } else {
        console.error('COI not found with ID:', coiNum);
        alert('COI not found');
        this.router.navigate(['/securehome/cois']);
      }
    } catch (error) {
      console.error('Error loading COI:', error);
      alert('Failed to load COI');
    } finally {
      this.loadingCoiData = false;
    }
  }

  populateFormWithCoi(coi: Coi) {
    //console.log('Populating form with COI:', coi);
    
    this.form.patchValue({
      coiId: coi.coiId,
      coiNum: coi.coiNum,
      mainDocumentName: coi.mainDocumentName ?? '',
      partyProvidingCoi: coi.partyProvidingCoi,
      insuredParty: coi.insuredParty,
      insuranceProvider: coi.insuranceProvider,
      legalEntities: coi.legalEntities,
      internalCoordinator: coi.internalCoordinator,
      primaryOwner: coi.primaryOwner,
      secondaryOwner: coi.secondaryOwner,
      tertiaryOwner: coi.tertiaryOwner,
      internalSignatory: coi.internalSignatory,
      coiCoordinator: coi.coiCoordinator,
      externalCoordinator: coi.externalCoordinator,
      carrierSignatory: coi.carrierSignatory,
      coverageTypes: coi.coverageTypes,
      status: coi.status,
      effectiveDate: coi.effectiveDate,
      expirationDate: coi.expirationDate,
      nextReviewDate: coi.nextReviewDate,
      lastReviewDate: coi.lastReviewDate,
      departments: coi.departments,
      sites: coi.sites,
      description: coi.description
    });
    
    // Handle existing file
    if (coi.linkToCoi) {
      this.loadDocument(coi.linkToCoi);
    }
  }

  // File Upload Methods
  // File Upload Methods moved to CreateEntity base class
  // (onDragOver, onDragLeave, onDrop, onFileSelected, handleFileUpload, removeFile)

  // Validation
  isFormValid(): boolean {
    const requiredFields = ['coiId', 'partyProvidingCoi', 'effectiveDate'];
    return requiredFields.every(field => this.form.get(field)?.value);
  }

  getValidationMessage(): string {
    if (!this.form.get('coiId')?.value) return 'COI ID is required';
    if (!this.form.get('partyProvidingCoi')?.value) return 'Party providing COI is required';
    if (!this.form.get('effectiveDate')?.value) return 'Effective date is required';
    return '';
  }

  async getStaticFieldValues() {
    this.statusTypes = await firstValueFrom(this.commonSheetsService.getContractStatusTypes());
    this.allCoverageTypes = await firstValueFrom(this.coiSheetsService.getCoverageTypes());
    this.allDepartments = await firstValueFrom(this.commonSheetsService.getDepartments());
  }

  displayUserFn(user: any): string {
    return user && user.friendlyName ? user.friendlyName : '';
  }

  displayPartyFn(party: any): string {
    return party && party.name ? party.name : '';
  }

  displayPersonFn(person: any): string {
    return person && person.fullName ? person.fullName : (person && person.friendlyName ? person.friendlyName : '');
  }

  displaySiteFn(site: any): string {
    if (!site) return '';
    if (typeof site === 'string') return site;
    return site.name || site.siteName || '';
  }

  displayLegalEntityFn(entity: any): string {
    if (!entity) return '';
    if (typeof entity === 'string') return entity;
    return entity.legalEntityName || entity.legalEntityData?.legalEntityName || entity.name || '';
  }

  // Override filter methods to work with Material Design autocomplete
  // Material sends Event objects with event.target.value, not { query: string } like PrimeNG
  filterParties(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.partyService.allParties$,
      'filteredParties',
      'name',
      query
    );
  }

  filterUsers(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.usersService.allUsers$,
      'filteredUsers',
      'friendlyName',
      query
    );
  }

  filterPeople(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.peopleService.allPeople$,
      'filteredPeople',
      'fullName',
      query
    );
  }

  filterLegalEntity(event: any): void {
    const query = event.target?.value || '';
    this.filterAndSetList(
      this.legalEntityService.allLegalEntities$,
      'filteredLegalEntities',
      'legalEntityName',
      query
    );
  }

  cancelSubmit() {
    this.router.navigateByUrl('/securehome/cois');
  }

  async uploadCoiFileToS3(file: File, coiId: string): Promise<string> {
    try {
      const fileExtension = file.name.split('.').pop();
      const uniqueFileName = `cois/${coiId}/${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExtension}`;

      const preSignedUrlResponse = await firstValueFrom(
        this.fileUploadService.getPresignedUrl(uniqueFileName)
      );
      const preSignedUrl = preSignedUrlResponse.presignedUrl;
      
      await firstValueFrom(
        this.fileUploadService.uploadFileToS3(preSignedUrl, file)
      );

      return preSignedUrl.split('?')[0];
      
    } catch (error) {
      console.error('S3 upload failed:', error);
      throw error;
    }
  }

  async onSubmit() {
    if (!this.isFormValid()) {
      alert(this.getValidationMessage());
      return;
    }

    this.saving = true;
    const coiToSave: Coi = this.form.value;

    if (!coiToSave.coiId) {
      alert('COI ID is missing');
      this.saving = false;
      return;
    }

    // Upload new file to S3 if present
    if (this.uploadedFile) {
      try {
        const fileUrl = await this.uploadCoiFileToS3(this.uploadedFile, coiToSave.coiId);
        coiToSave.linkToCoi = fileUrl;
      } catch (error) {
        console.error('Failed to upload file:', error);
        this.saving = false;
        alert('Failed to upload COI file. Please try again.');
        return;
      }
    } else if (this.currentDocument?.directLink) {
      coiToSave.linkToCoi = this.currentDocument.directLink;
    }

    const action = this.isEditMode ? DatabaseAction.Edit : DatabaseAction.Create;

    this.coiService.writeCoi(coiToSave).subscribe({
      next: async (response) => {
        //console.log('COI saved successfully', response);

        // Update relationships if needed
        if (this.isEditMode && this.originalCoi) {
          try {
            await this.updateCoiRelationships(coiToSave, this.originalCoi);
          } catch (relationshipError) {
            console.error('Failed to update relationships:', relationshipError);
            // Don't fail the entire save if relationships fail
          }
        }

        this.saving = false;
        this.router.navigate(['/securehome/coi', coiToSave.coiNum]);
      },
      error: (err) => {
        console.error('Failed to save COI:', err);
        this.saving = false;
        alert('Failed to save COI. Please try again.');
      }
    });
  }

  /**
   * Updates COI relationships when entities change
   */
  private async updateCoiRelationships(newCoi: Coi, oldCoi: Coi): Promise<void> {
    const coiRef = {
      type: 'coi' as const,
      id: newCoi.coiId!
    };

    // Track relationship fields that need updating
    const relationshipFields = [
      { field: 'partyProvidingCoi', oldValue: oldCoi.partyProvidingCoi, newValue: newCoi.partyProvidingCoi },
      { field: 'insuredParty', oldValue: oldCoi.insuredParty, newValue: newCoi.insuredParty },
      { field: 'insuranceProvider', oldValue: oldCoi.insuranceProvider, newValue: newCoi.insuranceProvider },
      { field: 'legalEntities', oldValue: oldCoi.legalEntities, newValue: newCoi.legalEntities }
    ];

    // Update each relationship field that changed
    for (const { field, oldValue, newValue } of relationshipFields) {
      // Skip if values are the same
      if (JSON.stringify(oldValue) === JSON.stringify(newValue)) {
        continue;
      }

      // Update the relationship
      await this.relationshipService.update(
        coiRef,
        field,
        oldValue,
        newValue
      );
    }

    //console.log('COI relationships updated successfully');
  }

  generateCoiId(): string {
    return generateCoiId();
  }
}